"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const decorators_1 = require("@powerednow/shared/modules/utilities/decorators");
const baseData_1 = __importDefault(require("./baseData"));
/**
 * This is used to track already fired _external_ events, like MODEL_DATA_REQUEST.
 * Theoretically `processedForwardedEvents` should be the only one we need and the extraneous logic
 * from `.on` can be removed, but let's do one big change at a time.
 */
const processedEvents = new WeakSet();
/**
 * With this we track events fired per-instance.
 */
const processedForwardedEvents = new WeakMap();
class ConnectedData extends baseData_1.default {
    /**
     * Default constructor dealing with the initialisation of the instance
     *
     * @param {DataObject} data
     * @param listeners
     */
    constructor(data, extraData = {}, { listeners = {}, cache = null, disposable = false, } = {
        listeners: {},
        cache: null,
        disposable: false,
    }) {
        super(data, null, null, disposable);
        this.whereToForward = {};
        this.eventOrigins = new Set();
        this.forwardedEventsRoot = {};
        this.associatedValueItems = {};
        this.cache = cache;
        this.addListenersFromMap(listeners);
        Object.values(ConnectedData.EVENTS).forEach(eventName => {
            const BOUND_FOR_FORWARDING = this.eventForwardingFunction(eventName);
            this.on(eventName, BOUND_FOR_FORWARDING);
        });
    }
    on(event, listener) {
        let isExternalEvent = false;
        if (typeof event === 'string') {
            isExternalEvent = this.constructor.EXTERNAL_EVENTS.includes(event);
        }
        if (isExternalEvent && !listener.isEventForwardingFunction) {
            /**
             * We must capture the proper scope of the original handler, you must not use arrow function.
             */
            return super.on(event, function externalEventHandlerProxy(params) {
                if (processedEvents.has(params)) {
                    return;
                }
                listener.call(this, params);
                processedEvents.add(params);
            });
        }
        return super.on(event, listener);
    }
    emit(event, eventArgs) {
        return super.emit(event, eventArgs);
    }
    /**
     * We always bind a number of event listeners to every complexData instances. The listener is the same for every event handler,
     * but those event handlers are generated dynamically, the only difference is their name. In the past we used to return an
     * arrow function, which captured the scope of the calling function and bind to the outer function. While it was working
     * that way we created different event handler function for every complexdata instances.
     *
     * As EventEmitter properly propagates scope, we can actually create event handlers once per eventName and simply return
     * the same generated normal function. It is important to _not_ return an arrow function, as it would capture the
     * scope of the first invoking instance, while we want scope resolution per-call.
     *
     *
     * @param eventName
     * @private
     */
    eventForwardingFunction(eventName) {
        function generatedEventForwardingFunction(eventArgs) {
            if (!this.whereToForward[eventName]) {
                return;
            }
            //
            // Forward this event to all subscribers but only if they haven't got it already
            //
            let localProcessedEventsSet = processedForwardedEvents.get(this);
            if (!localProcessedEventsSet) {
                localProcessedEventsSet = new WeakSet();
                processedForwardedEvents.set(this, localProcessedEventsSet);
            }
            localProcessedEventsSet.add(eventArgs);
            this.whereToForward[eventName].forEach(forwardTarget => {
                let forwardedTargetEventsSet = processedForwardedEvents.get(forwardTarget);
                if (!forwardedTargetEventsSet) {
                    forwardedTargetEventsSet = new WeakSet();
                    processedForwardedEvents.set(forwardedTargetEventsSet, forwardedTargetEventsSet);
                }
                if (!forwardedTargetEventsSet.has(eventArgs)) {
                    forwardedTargetEventsSet.add(eventArgs);
                    forwardTarget.emit(eventName, eventArgs);
                }
            });
        }
        generatedEventForwardingFunction.isEventForwardingFunction = true;
        return generatedEventForwardingFunction;
    }
    /**
     * Generate a random event name from the class name and three
     * random numbers what will be very likely unique.
     *
     * @returns {string}
     */
    static generateRandomEventName() {
        return `${this.name}-${Math.random()}${Math.random()}${Math.random()}`;
    }
    /**
     * Returns true if the given event got an external event handler assigned.
     *
     * @param {string} eventName
     * @returns {Boolean}
     */
    isListenerBound(eventName) {
        const rootEventHandler = typeof this.forwardedEventsRoot[eventName] !== 'undefined' ? this.forwardedEventsRoot[eventName] : this;
        const eventProps = rootEventHandler._events[eventName];
        return typeof eventProps !== 'undefined'
            && (Array.isArray(eventProps) ? eventProps : [eventProps]).some(item => item.isEventForwardingFunction !== true);
    }
    addListenersFromMap(listeners) {
        Object.entries(listeners).forEach(([eventName, eventHandler]) => {
            (Array.isArray(eventHandler) ? eventHandler : [eventHandler]).forEach(handler => {
                this.on(eventName, handler);
            });
        });
    }
    /**
     * Copy selected listeners from source to this
     *
     * @param {ConnectedData} source
     * @param {string[]} selectedEvents
     */
    copyListenersFrom(source, selectedEvents = []) {
        this.addListenersFromMap(source.getListenerMap(selectedEvents));
    }
    /**
     * Sets a new cache for this object
     *
     * @param {Cache} cache
     */
    setCache(cache) {
        this.cache = cache;
    }
    /**
     * Get an instance from the cache if it is already there.
     * Otherwise returns null.
     *
     * @param {DataObject} itemData
     * @param {string} modelName
     * @returns {any}
     */
    getInstanceFromCache(itemData, modelName) {
        if (this.cache) {
            const fromCache = this.cache.get(modelName, itemData.id);
            return fromCache;
        }
        return null;
    }
    /**
     * Generate a map of listeners assigned to this object
     *
     * @param {string[]} selectedEvents
     * @returns {{}}
     */
    getListenerMap(selectedEvents = [], getListeners = event => this.listeners(event)) {
        const events = selectedEvents.length !== 0 ? selectedEvents : Object.keys(this._events);
        return events.reduce((accumulator, event) => {
            const listeners = getListeners(event);
            if (listeners && listeners.length > 0) {
                accumulator[event] = listeners;
            }
            return accumulator;
        }, {});
    }
    getRootListenerMap(selectedEvents = []) {
        return this.getListenerMap(selectedEvents, event => {
            const listenersRoot = this.forwardedEventsRoot[event] || this;
            return listenersRoot.listeners(event);
        });
    }
    /**
     * Set up forwarded event between two instances. All events of the passed in
     * instance will be forwarded to this instance.
     *
     * @param {this} instance
     */
    setupEventForwarding(instance) {
        if (this.eventOrigins.has(instance)) {
            return;
        }
        this.eventOrigins.add(instance);
        ConnectedData.FORWARDING_EVENTS
            .forEach(eventName => {
            if (!instance.forwardedEventsRoot[eventName]) {
                if (this.forwardedEventsRoot[eventName]) {
                    this.forwardedEventsRoot[eventName].eventOrigins.add(instance);
                    instance.forwardedEventsRoot[eventName] = this.forwardedEventsRoot[eventName];
                }
                else {
                    instance.forwardedEventsRoot[eventName] = this;
                }
            }
            instance.whereToForward[eventName] = instance.whereToForward[eventName] || new Set();
            instance.whereToForward[eventName].add(this);
        });
    }
    /**
     * Remove all event forwarding event handlers from the passed
     * in instance.
     *
     * @param {ConnectedData} instance
     */
    removeFromEventForwarding(instance) {
        this.eventOrigins.delete(instance);
        Object.entries(instance.whereToForward).forEach(([eventName, itemsToForward]) => {
            if (instance.forwardedEventsRoot[eventName] === this) {
                delete instance.forwardedEventsRoot[eventName];
            }
            itemsToForward.delete(this);
        });
        [...this.eventOrigins].forEach((origin) => {
            if (instance.eventOrigins.has(origin)) {
                instance.eventOrigins.delete(origin);
                instance.setupEventForwarding(origin);
            }
        });
    }
    /**
     * Fire data event to be processed by the wrapper and wait for its pair response event
     *
     * @param {string} eventName
     * @param eventParams
     * @returns {Promise<any>}
     */
    async fireAndHandleEvent(eventName, eventParams = {}) {
        if (!this.isListenerBound(eventName)) {
            return Promise.resolve();
        }
        const temporaryEventName = this.constructor.generateRandomEventName();
        const retPromise = new Promise((resolve, reject) => {
            this.once(temporaryEventName, eventResponse => {
                if (eventResponse instanceof Error) {
                    return reject(eventResponse);
                }
                return resolve(eventResponse);
            });
        });
        this.emit(eventName, {
            instance: this,
            responseEventName: temporaryEventName,
            ...eventParams,
        });
        return retPromise;
    }
    /**
     * Load external data from the attached wrapper if there is any.
     *
     * @param {string} associatedValuesKey
     * @param {GetAssociatedOptions} remoteOptions
     * @returns {Promise<{responseData?: DataObject[]}>}
     */
    loadAssociatedData(associatedValuesKey, remoteOptions = {}) {
        return this.fireAndHandleEvent(baseData_1.default.EVENTS.MODEL_DATA_REQUEST, { associatedValuesKey, remoteOptions });
    }
    filterAssociatedData(associatedValuesKey, remoteOptions = {}) {
        return this.fireAndHandleEvent(baseData_1.default.EVENTS.MODEL_DATA_REQUEST, {
            associatedValuesKey,
            remoteOptions,
        });
    }
    searchStringByIndex(searchTerm, searchModelMap, searchPerformer, matchOnly) {
        return this.fireAndHandleEvent(baseData_1.default.EVENTS.MODEL_FULLTEXT_SEARCH, {
            remoteOptions: {
                searchTerm, searchModelMap, searchPerformer, matchOnly,
            },
        });
    }
    async callExternalFunction(functionName, args) {
        const result = await this.fireAndHandleEvent(baseData_1.default.EVENTS.FUNCTION_CALL_REQUEST, { remoteOptions: { functionName, args } });
        return result.responseData;
    }
    /**
     * Load external generic data by using the attached wrapper if there is any
     */
    loadGenericData(Source, filters = {}) {
        const remoteOptions = {
            filters: this.conditionToFilter(filters),
        };
        return this.fireAndHandleEvent(baseData_1.default.EVENTS.MODEL_DATA_REQUEST, { Source, remoteOptions });
    }
    conditionToFilter(conditions) {
        return Object.entries(conditions).reduce((acc, item) => {
            const [key, value] = item;
            if (key === '$or') {
                value.forEach(part => {
                    Object.entries(part).forEach(([orKey, orValue]) => {
                        acc.push({
                            logic: 'or',
                            operator: '=',
                            property: orKey,
                            value: (typeof orValue === 'symbol') ? this.data[this.constructor.Entity.fieldSymbolValues[orValue]] : orValue,
                        });
                    });
                });
            }
            else {
                acc.push({
                    operator: '=',
                    property: key,
                    value,
                });
            }
            return acc;
        }, []);
    }
}
exports.default = ConnectedData;
__decorate([
    (0, decorators_1.memoize)()
], ConnectedData.prototype, "eventForwardingFunction", null);
//# sourceMappingURL=../../src/connectedData.js.map